import type { RecordFormField } from "features/shared/components/dynamic-form/types";
import type { TFunction } from "i18next";

import { FilterTypes } from "enums/components";
import {
  buildRecordFormFields,
  buildRecordFormTabs,
  getRecordFormFieldLayoutProps,
} from "features/shared/lib/technical-config-tab/recordFormFields";

import type { WindowCatalogItem } from "../model/types";

import {
  getHeaderFieldDefinitions,
  getTabFieldDefinitions,
  getWindowConfigTabs,
  type HeaderFieldDefinition,
  WindowConfigFormState,
} from "./windowConfigFieldDefinitions";
import {
  getFrameTypesForWindowType,
  getShadingSystemTypeFormDisplayValue,
  isShadingLocationSelected,
  isShadingSystemSelected,
  type ShadingCatalogMatchResult,
  type ShadingSystemTypeSuggestions,
} from "./windowConfigOptions";

const getFieldLabelText = (field: { key: string; labelKey?: string }, t: TFunction) =>
  String(t(`TechnicalData.windows.fields.${field.labelKey ?? field.key}`));

const buildHeaderField = (
  field: HeaderFieldDefinition,
  formState: WindowConfigFormState,
  catalog: WindowCatalogItem[],
  onFieldChange: (field: keyof WindowConfigFormState | string, value: unknown) => void,
  t: TFunction
): RecordFormField => {
  const windowTypeNames = [...new Set(catalog.map((item) => item.windowTypeName))].sort();
  const frameTypes = getFrameTypesForWindowType(catalog, formState.windowTypeName);

  const options = field.key === "windowTypeName" ? windowTypeNames : field.key === "frameType" ? frameTypes : [];

  return {
    colSpan: field.colSpan ?? 1,
    config: formState,
    filterName: field.key,
    filterType: FilterTypes.AUTOCOMPLETE_SINGLE,
    props: {
      label: getFieldLabelText(field, t),
      ...getRecordFormFieldLayoutProps(FilterTypes.AUTOCOMPLETE_SINGLE),
      onCommit: (value: string) => onFieldChange(`${field.key}__commit`, value),
      onFocus: (value: string) => onFieldChange(`${field.key}__focus`, value),
      options,
      placeholder: getFieldLabelText(field, t),
      type: field.inputType,
    },
    setConfig: (_, newValue) => onFieldChange(field.key, newValue),
  };
};

type BuildWindowConfigDrawerConfigOptions = {
  canSelectShadingType: boolean;
  catalog: WindowCatalogItem[];
  formState: WindowConfigFormState;
  hasWindowTypeAndFrameType: boolean;
  noneLocationLabel: string;
  onFieldChange: (field: keyof WindowConfigFormState | string, value: unknown) => void;
  shadingCatalogMatch: ShadingCatalogMatchResult;
  shadingLocationOptions: { displayOptions: string[]; options: string[] };
  shadingSystemTypeSuggestions: ShadingSystemTypeSuggestions;
  t: TFunction;
};

export const buildWindowConfigDrawerConfig = ({
  canSelectShadingType,
  catalog,
  formState,
  hasWindowTypeAndFrameType,
  noneLocationLabel,
  onFieldChange,
  shadingCatalogMatch,
  shadingLocationOptions,
  shadingSystemTypeSuggestions,
  t,
}: BuildWindowConfigDrawerConfigOptions) => {
  const headerFields = getHeaderFieldDefinitions().map((field) =>
    buildHeaderField(field, formState, catalog, onFieldChange, t)
  );

  const hasShadingSystem = isShadingSystemSelected(formState.shadingSystemType);

  const tabFields = buildRecordFormFields({
    definitions: getTabFieldDefinitions(),
    formState,
    getDrawerOptions: (field) => {
      if (field.key === "shadingLocation") {
        return shadingLocationOptions;
      }

      if (field.key === "shadingSystemType") {
        return {
          displayOptions: shadingSystemTypeSuggestions.displayOptions,
          options: shadingSystemTypeSuggestions.options,
        };
      }

      return undefined;
    },
    getLabel: (field) => getFieldLabelText(field, t),
    onFieldChange,
  }).map((field) => {
    if (field.filterName === "gValueDirectWindow" || field.filterName === "gValueDiffuseWindow") {
      return {
        ...field,
        props: {
          ...field.props,
          disabled: hasShadingSystem,
        },
      };
    }

    if (field.filterName === "gValueDirectWindowShading" || field.filterName === "gValueDiffuseWindowShading") {
      return {
        ...field,
        props: {
          ...field.props,
          disabled: !hasShadingSystem,
        },
      };
    }

    if (field.tab === "shading" && field.filterName === "shadingLocation") {
      return {
        ...field,
        config: {
          ...formState,
          shadingLocation: isShadingLocationSelected(formState.shadingLocation) ? formState.shadingLocation : undefined,
        },
        props: {
          ...field.props,
          clearable: false,
          disabled: !hasWindowTypeAndFrameType,
        },
      };
    }

    if (field.tab !== "shading" || field.filterName !== "shadingSystemType") return field;

    return {
      ...field,
      config: {
        ...formState,
        shadingSystemType: getShadingSystemTypeFormDisplayValue(
          formState.shadingSystemType,
          shadingSystemTypeSuggestions,
          shadingCatalogMatch,
          catalog,
          formState,
          noneLocationLabel
        ),
      },
      props: {
        ...field.props,
        clearable: false,
        disabled: !canSelectShadingType,
      },
    };
  });

  return {
    fields: [...headerFields, ...tabFields],
    tabs: buildRecordFormTabs(getWindowConfigTabs(), t, "TechnicalData.windows.tabs"),
  };
};
